/**
 * CompositeChartPage — страница композитной карты
 *
 * Группа 3.2: Композитная карта (midpoint method)
 * - Селектор 2 контактов
 * - Кнопка "Рассчитать"
 * - Результат: планеты в знаках, аспекты, AI-интерпретация
 * - Тёмная тема, gold акценты
 */

import React, { useState, useEffect } from 'react';
import ContactSelector, { ExtendedContact } from '../../components/ContactSelector';
import { api } from '../../api';
import { handleApiError } from '../../utils/apiErrors';
import { logger } from '../../utils/logger';

// ============================================================
// Types
// ============================================================

interface CompositePlanet {
  name: string;
  nameRu: string;
  symbol: string;
  sign: string;
  signRu: string;
  longitude: number;
  house: number;
}

interface CompositeAspect {
  planetA: string;
  planetARu: string;
  planetB: string;
  planetBRu: string;
  type: string;
  orb: number;
}

interface CompositeChartResult {
  planets: CompositePlanet[];
  houses: Array<{ number: number; sign: string; signRu: string; longitude: number }>;
  aspects: CompositeAspect[];
  aiInterpretation: string;
  ascendant: { sign: string; signRu: string; longitude: number };
  mc: { sign: string; signRu: string; longitude: number };
}

// ============================================================
// Constants
// ============================================================

const ASPECT_COLORS: Record<string, string> = {
  'Соединение': '#FFD700',
  'Оппозиция': '#FF4444',
  'Трин': '#44BB44',
  'Квадрат': '#FF6600',
  'Секстиль': '#4488FF',
  'Квинконс': '#AA44FF',
  'Полусекстиль': '#88AACC',
  'Полуквадрат': '#CC8844',
  'Сесквадрат': '#CC6688',
  'Квинтиль': '#44CCAA',
  'Биквинтиль': '#66CCAA',
};

const PLANET_SYMBOLS: Record<string, string> = {
  Sun: '☉', Moon: '☽', Mercury: '☿', Venus: '♀', Mars: '♂',
  Jupiter: '♃', Saturn: '♄', Uranus: '♅', Neptune: '♆', Pluto: '♇',
  Chiron: '⚷', Lilith: '⚸', NorthNode: '☊', SouthNode: '☋',
};

// ============================================================
// Styles — Astranum design standard
// ============================================================

const colors = {
  bg: '#0a0814',
  bgCard: 'rgba(13, 20, 40, 0.85)',
  border: 'rgba(212, 175, 55, 0.12)',
  gold: '#d4af37',
  goldLight: '#f4e4a6',
  text: '#f0f0f0',
  textMuted: '#b0b0b0',
  error: '#ff4444',
  success: '#44BB44',
};

const radius = { sm: '8px', md: '12px', lg: '16px', xl: '20px', xxl: '24px' };
const spacing = { xs: '4px', sm: '8px', md: '12px', lg: '16px', xl: '24px', xxl: '32px' };
const fontSize = { sm: '13px', md: '14px', lg: '16px', xl: '18px', xxl: '24px' };

const goldAccentLine: React.CSSProperties = {
  position: 'absolute',
  top: 0,
  left: '10%',
  right: '10%',
  height: '2px',
  background: 'linear-gradient(90deg, transparent, #d4af37, transparent)',
};

const cardStyle = (isMobile: boolean): React.CSSProperties => ({
  background: colors.bgCard,
  backdropFilter: 'blur(20px)',
  border: `1px solid ${colors.border}`,
  borderRadius: radius.xxl,
  padding: isMobile ? '16px' : spacing.lg,
  marginBottom: spacing.lg,
  boxShadow: '0 8px 40px rgba(212, 175, 55, 0.08), inset 0 0 40px rgba(13, 20, 40, 0.5)',
});

const cardTitleStyle: React.CSSProperties = {
  fontSize: fontSize.lg,
  fontWeight: 'bold',
  color: colors.gold,
  marginBottom: spacing.md,
  fontFamily: 'var(--font-heading)',
};

const planetGridStyle: React.CSSProperties = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
  gap: spacing.sm,
};

const planetItemStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: spacing.sm,
  padding: spacing.sm,
  background: 'rgba(255, 255, 255, 0.03)',
  borderRadius: radius.sm,
  border: '1px solid rgba(255, 255, 255, 0.05)',
};

const aspectItemStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: spacing.sm,
  padding: spacing.sm,
  background: 'rgba(255, 255, 255, 0.02)',
  borderRadius: radius.sm,
  marginBottom: spacing.xs,
};

const aspectDotStyle = (color: string): React.CSSProperties => ({
  width: '10px',
  height: '10px',
  borderRadius: '50%',
  background: color,
  flexShrink: 0,
});

const interpretationStyle: React.CSSProperties = {
  fontSize: fontSize.md,
  lineHeight: '1.7',
  color: colors.text,
  whiteSpace: 'pre-wrap',
};

const loadingStyle: React.CSSProperties = {
  textAlign: 'center',
  padding: spacing.xxl,
  color: colors.gold,
  fontSize: fontSize.lg,
};

const errorStyle: React.CSSProperties = {
  textAlign: 'center',
  padding: spacing.lg,
  color: colors.error,
  background: 'rgba(255, 68, 68, 0.1)',
  borderRadius: radius.md,
  border: `1px solid rgba(255, 68, 68, 0.3)`,
};

// ============================================================
// Component
// ============================================================

const CompositeChartPage: React.FC = () => {
  const [contact1, setContact1] = useState<ExtendedContact | null>(null);
  const [contact2, setContact2] = useState<ExtendedContact | null>(null);
  const [result, setResult] = useState<CompositeChartResult | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const check = () => setIsMobile(window.innerWidth <= 768);
    check();
    window.addEventListener('resize', check);
    return () => window.removeEventListener('resize', check);
  }, []);

  const canCalculate = contact1 !== null && contact2 !== null && contact1.id !== contact2.id;

  const handleCalculate = async () => {
    if (!canCalculate || !contact1 || !contact2) return;

    setLoading(true);
    setError(null);
    setResult(null);

    try {
      const res = await api.post<CompositeChartResult>('/composite/calculate', {
        contactId1: contact1.id,
        contactId2: contact2.id,
      });

      if (res.success && res.data) {
        setResult(res.data);
      } else {
        setError(res.message || 'Не удалось рассчитать композитную карту');
      }
    } catch (err) {
      const message = handleApiError(err, 'Ошибка при расчёте композитной карты');
      setError(message);
      logger.error('CompositeChartPage calculate error', err);
    } finally {
      setLoading(false);
    }
  };

  const pageStyle: React.CSSProperties = {
    maxWidth: '900px',
    margin: '0 auto',
    padding: isMobile ? '20px 12px' : `${spacing.xxl} ${spacing.lg}`,
    fontFamily: 'var(--font-body)',
    color: colors.text,
    position: 'relative',
  };

  const headerStyle: React.CSSProperties = {
    textAlign: 'center',
    marginBottom: isMobile ? '20px' : spacing.xxl,
  };

  const titleStyle: React.CSSProperties = {
    fontSize: isMobile ? '20px' : fontSize.xxl,
    fontWeight: 'bold',
    fontFamily: 'var(--font-display)',
    fontStyle: 'italic',
    background: 'linear-gradient(135deg, #F4E4A6 0%, #D4AF37 40%, #B8942B 70%, #F4E4A6 100%)',
    backgroundSize: '200% auto',
    WebkitBackgroundClip: 'text',
    WebkitTextFillColor: 'transparent',
    backgroundClip: 'text',
    marginBottom: spacing.sm,
  };

  const subtitleStyle: React.CSSProperties = {
    fontSize: isMobile ? '13px' : fontSize.md,
    color: colors.textMuted,
    margin: 0,
  };

  const selectorSectionStyle: React.CSSProperties = {
    display: 'grid',
    gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr',
    gap: spacing.lg,
    marginBottom: spacing.xl,
  };

  const selectorLabelStyle: React.CSSProperties = {
    fontSize: isMobile ? '13px' : fontSize.md,
    color: colors.gold,
    fontWeight: 'bold',
    marginBottom: spacing.sm,
    fontFamily: 'var(--font-sans)',
  };

  const calculateBtnStyle: React.CSSProperties = {
    display: 'block',
    width: '100%',
    maxWidth: isMobile ? '100%' : '300px',
    margin: '0 auto',
    padding: isMobile ? '12px 20px' : '14px 28px',
    background: 'linear-gradient(135deg, #d4af37 0%, #f4e4a6 100%)',
    color: '#0a0814',
    border: 'none',
    borderRadius: radius.md,
    fontSize: isMobile ? '14px' : fontSize.lg,
    fontWeight: 'bold',
    cursor: 'pointer',
    fontFamily: 'var(--font-sans)',
    transition: 'all 0.2s ease',
  };

  const calculateBtnDisabledStyle: React.CSSProperties = {
    ...calculateBtnStyle,
    opacity: 0.5,
    cursor: 'not-allowed',
  };

  return (
    <div style={pageStyle}>
      <div style={goldAccentLine} />

      {/* Header */}
      <div style={headerStyle}>
        <h1 style={titleStyle}>Композитная карта</h1>
        <p style={subtitleStyle}>
          Третья карта отношений — средние точки двух натальных карт
        </p>
      </div>

      {/* Contact Selectors */}
      <div style={selectorSectionStyle}>
        <div>
          <div style={selectorLabelStyle}>Партнёр A</div>
          <ContactSelector
            onSelect={setContact1}
            selectedId={contact1?.id}
            placeholder="Выберите первого партнёра..."
          />
        </div>
        <div>
          <div style={selectorLabelStyle}>Партнёр B</div>
          <ContactSelector
            onSelect={setContact2}
            selectedId={contact2?.id}
            placeholder="Выберите второго партнёра..."
          />
        </div>
      </div>

      {/* Calculate Button */}
      <button
        onClick={handleCalculate}
        disabled={!canCalculate || loading}
        style={canCalculate && !loading ? calculateBtnStyle : calculateBtnDisabledStyle}
        onMouseEnter={(e) => {
          if (canCalculate && !loading) {
            (e.currentTarget as HTMLButtonElement).style.transform = 'scale(1.02)';
            (e.currentTarget as HTMLButtonElement).style.boxShadow = `0 0 20px ${colors.gold}40`;
          }
        }}
        onMouseLeave={(e) => {
          (e.currentTarget as HTMLButtonElement).style.transform = 'scale(1)';
          (e.currentTarget as HTMLButtonElement).style.boxShadow = 'none';
        }}
      >
        {loading ? 'Расчёт...' : 'Рассчитать'}
      </button>

      {/* Error */}
      {error && (
        <div style={{ ...errorStyle, marginTop: spacing.xl }}>
          {error}
        </div>
      )}

      {/* Loading */}
      {loading && (
        <div style={loadingStyle}>
          <div style={{ marginBottom: spacing.md }}>Рассчитываем композитную карту...</div>
          <div style={{ fontSize: fontSize.sm, color: colors.textMuted }}>
            Это может занять до 30 секунд
          </div>
        </div>
      )}

      {/* Results */}
      {result && !loading && (
        <div style={{ marginTop: spacing.xxl }}>
          {/* Key Points */}
          <div style={cardStyle(isMobile)}>
            <h2 style={cardTitleStyle}>Ключевые позиции</h2>
            <div style={{ display: 'flex', gap: spacing.lg, flexWrap: 'wrap' }}>
              <div>
                <span style={{ color: colors.textMuted, fontSize: fontSize.sm }}>Асцендент:</span>
                {' '}
                <span style={{ color: colors.gold, fontWeight: 'bold' }}>
                  {result.ascendant.signRu}
                </span>
              </div>
              <div>
                <span style={{ color: colors.textMuted, fontSize: fontSize.sm }}>MC:</span>
                {' '}
                <span style={{ color: colors.gold, fontWeight: 'bold' }}>
                  {result.mc.signRu}
                </span>
              </div>
            </div>
          </div>

          {/* Planets */}
          <div style={cardStyle(isMobile)}>
            <h2 style={cardTitleStyle}>Планеты композитной карты</h2>
            <div style={planetGridStyle}>
              {result.planets.map((planet) => (
                <div key={planet.name} style={planetItemStyle}>
                  <span style={{ fontSize: '18px', width: '28px', textAlign: 'center' }}>
                    {PLANET_SYMBOLS[planet.name] || planet.symbol}
                  </span>
                  <div>
                    <div style={{ color: colors.text, fontSize: fontSize.sm, fontWeight: 'bold' }}>
                      {planet.nameRu}
                    </div>
                    <div style={{ color: colors.goldLight, fontSize: fontSize.sm }}>
                      {planet.signRu}
                    </div>
                    <div style={{ color: colors.textMuted, fontSize: '11px' }}>
                      {planet.house} дом
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Aspects */}
          <div style={cardStyle(isMobile)}>
            <h2 style={cardTitleStyle}>Аспекты ({result.aspects.length})</h2>
            {result.aspects.length === 0 ? (
              <div style={{ color: colors.textMuted, textAlign: 'center', padding: spacing.lg }}>
                Точных аспектов не обнаружено
              </div>
            ) : (
              result.aspects.map((aspect, i) => {
                const color = ASPECT_COLORS[aspect.type] || '#888';
                return (
                  <div key={i} style={aspectItemStyle}>
                    <div style={aspectDotStyle(color)} />
                    <span style={{ color: colors.text, fontSize: fontSize.sm }}>
                      {aspect.planetARu}
                    </span>
                    <span style={{ color, fontSize: fontSize.sm, fontWeight: 'bold', margin: `0 ${spacing.xs}` }}>
                      {aspect.type}
                    </span>
                    <span style={{ color: colors.text, fontSize: fontSize.sm }}>
                      {aspect.planetBRu}
                    </span>
                    <span style={{ color: colors.textMuted, fontSize: '11px', marginLeft: 'auto' }}>
                      орб. {aspect.orb}°
                    </span>
                  </div>
                );
              })
            )}
          </div>

          {/* AI Interpretation */}
          <div style={cardStyle(isMobile)}>
            <h2 style={cardTitleStyle}>Интерпретация</h2>
            <div style={interpretationStyle}>
              {result.aiInterpretation}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CompositeChartPage;
